import { z } from "zod";
import { AgencyIdSchema, ProjectIdSchema, RevisionIdSchema } from "../primitives/ids.js";
/**
 * Gate 2 Phase 2 — BusinessGraph contradiction result (computed, not persisted).
 * schemaId: businessgraph.contradictionResult.v1
 */
export const BusinessGraphFactStateSchema = z.enum([
    "ACTIVE",
    "CANDIDATE",
    "REJECTED",
    "SUPERSEDED",
]);
/** Facts eligible for active comparison (rejected/superseded excluded). */
export const COMPARABLE_FACT_STATES = [
    "ACTIVE",
    "CANDIDATE",
];
export const BusinessGraphContradictionSeveritySchema = z.enum(["WARN", "BLOCK"]);
export const BusinessGraphContradictionStatusSchema = z.enum([
    "CLEAR",
    "WARNINGS",
    "BLOCKED",
    /** Malformed / mixed / foreign tenant input — never a clean pass. */
    "INVALID_INPUT",
]);
/** Stable machine codes — do not rename without migration of clients. */
export const BusinessGraphContradictionCodeSchema = z.enum([
    "CONTACT_PHONE_CONFLICT",
    "CONTACT_EMAIL_CONFLICT",
    "BUSINESS_NAME_CONFLICT",
    "HOURS_DAY_CONFLICT",
    "SERVICE_ID_NAME_CONFLICT",
    "SERVICE_AREA_ID_NAME_CONFLICT",
]);
export const BusinessGraphComparableFactV1Schema = z
    .object({
    factId: z.string().min(1),
    fieldPath: z.string().min(1),
    /** Raw display-safe value as supplied. */
    value: z.string(),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    state: BusinessGraphFactStateSchema,
    /** Scope discriminator (day, service id, area id). Empty = global field. */
    scopeKey: z.string().default(""),
    sourceRef: z.string().optional(),
    revisionId: RevisionIdSchema.optional(),
    candidateId: z.string().optional(),
})
    .strict();
export const BusinessGraphContradictionItemV1Schema = z
    .object({
    contradictionId: z.string().min(1),
    code: BusinessGraphContradictionCodeSchema,
    severity: BusinessGraphContradictionSeveritySchema,
    fieldPath: z.string().min(1),
    scopeKey: z.string(),
    normalizedValues: z.array(z.string()).min(2),
    displayValues: z.array(z.string()).min(2),
    sourceRefs: z.array(z.string()),
    revisionIds: z.array(z.string()),
    candidateIds: z.array(z.string()),
    factIds: z.array(z.string()).min(2),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    blocking: z.boolean(),
    readinessImpact: z
        .object({
        blocksGeneration: z.boolean(),
        blocksApproval: z.boolean(),
        affectedOutputs: z.array(z.string()),
    })
        .strict(),
    message: z.string().min(1),
    suggestedResolution: z.string().min(1),
    sortKey: z.string().min(1),
})
    .strict();
export const BusinessGraphContradictionResultV1Schema = z
    .object({
    schemaVersion: z.literal("businessgraph.contradictionResult.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    revisionId: z.string().optional(),
    evaluatedFromBusinessGraphSnapshotHash: z.string().optional(),
    checkedAt: z.string().datetime(),
    status: BusinessGraphContradictionStatusSchema,
    contradictions: z.array(BusinessGraphContradictionItemV1Schema),
    readinessImpact: z
        .object({
        blocksGeneration: z.boolean(),
        blocksApproval: z.boolean(),
        affectedOutputs: z.array(z.string()),
    })
        .strict(),
    /** False when detection ran; disabled/skipped must not pretend CLEAR. */
    detectionCompleted: z.literal(true),
})
    .strict();
export const FIELD_PATH_PRIMARY_PHONE = "business.primaryPhone";
export const FIELD_PATH_PRIMARY_EMAIL = "business.primaryEmail";
export const FIELD_PATH_BUSINESS_NAME = "business.businessName";
export const FIELD_PATH_HOURS = "operations.hours";
export const FIELD_PATH_SERVICE_NAME = "services.name";
export const FIELD_PATH_SERVICE_AREA_NAME = "operations.serviceAreas.name";
//# sourceMappingURL=business-graph-contradiction.js.map